import {FilterEnum} from "Agent/AgentForms/Marketing/ducks/Marketing";
import {AsyncPage, Empty, isLoaded} from 'Core/models/Async';
import {FilterOptionUnion, FilterOptionValue} from "Core/models/FilterOption";
import {Page} from 'Core/models/Page';
import * as O from "fp-ts/lib/Option";
import {Sort} from "Shared/components/PagedView/Table/Sort";
import {createAction} from 'Shared/utilities/actionCreator';
import {UserClaims} from 'Users/models/Claims';
import {InteractiveForm} from '../models/Interactive';

export enum InteractiveFormActionType {
    GET_DATA = 'interactive/GET_INTERACTIVE_FORM_DATA',
    CHANGE_DATA = 'interactive/CHANGE_INTERACTIVE_FORM_DATA',
    CHANGE_INTERACTIVE_FILTERS = 'interactive/CHANGE_INTERACTIVE_FILTER',
    CHANGE_SORT = 'interactive/CHANGE_SORT',
    CLEAR_SORT = 'interactive/CLEAR_SORT',
    CLEAR_INTERACTIVE = 'interactive/CLEAR_INTERACTIVE',
}

export type InteractiveFilters = {
    states: FilterOptionUnion,
    type: FilterOptionUnion,
    product: string,
    category: FilterOptionUnion,
    formNumber: string,
    description: string,
    pageNumber: string,
    pageSize: string,
};

export const EmptyInteractiveFilter: InteractiveFilters = {
    states: FilterOptionValue.EmptyOption(),
    type: FilterOptionValue.EmptyOption(),
    product: '',
    category: FilterOptionValue.EmptyOption(),
    formNumber: '',
    description: '',
    pageNumber: '1',
    pageSize: '25',
};

export const changeInteractiveFilters = (filter: InteractiveFilters) =>
    createAction(InteractiveFormActionType.CHANGE_INTERACTIVE_FILTERS, {filter});

export const changeSort = (sort?: Sort) => createAction(InteractiveFormActionType.CHANGE_SORT, {sort});
export const clearSort = () => createAction(InteractiveFormActionType.CLEAR_SORT);

export const clearInteractive = (filter: InteractiveFilters) => createAction(InteractiveFormActionType.CLEAR_INTERACTIVE, {filter});

export const getInteractiveFormData = (
    claims: UserClaims,
    page?: Page<InteractiveForm>,
    isNewYork?: boolean,
    filter?: InteractiveFilters,
    sort?: Sort
) =>
    createAction(InteractiveFormActionType.GET_DATA, {
        claims,
        page,
        isNewYork,
        filter,
        sort,
    });

export const changeInteractiveFormData = (data: AsyncPage<Page<InteractiveForm>>) => createAction(InteractiveFormActionType.CHANGE_DATA, data);

type InteractiveFormState = {
    data: AsyncPage<Page<InteractiveForm>>;
    filter: InteractiveFilters,
    sort: O.Option<Sort>,
    interactiveFormsCategories: FilterEnum[];
    interactiveFormsType: FilterEnum[];
};

export const DEFAULT_STATE: InteractiveFormState = {
    data: new Empty(),
    filter: EmptyInteractiveFilter,
    sort: O.fromNullable({column: 'product', direction: 'asc'}),
    interactiveFormsCategories: [],
    interactiveFormsType: [],
};

type InteractiveFormAction =
    | ReturnType<typeof clearSort>
    | ReturnType<typeof clearInteractive>
    | ReturnType<typeof changeSort>
    | ReturnType<typeof getInteractiveFormData>
    | ReturnType<typeof changeInteractiveFormData>
    | ReturnType<typeof changeInteractiveFilters>;

export default (state: InteractiveFormState , action: InteractiveFormAction): InteractiveFormState => {
    const currentState = state || DEFAULT_STATE;
    switch (action.type) {
        case InteractiveFormActionType.CHANGE_DATA:
            return {
                ...currentState,
                data: action.payload,
                interactiveFormsCategories: isLoaded(action.payload) ? action.payload.Data.interactiveFormsCategories! : [],
                interactiveFormsType: isLoaded(action.payload) ? action.payload.Data.interactiveFormsType! : [],
            };
        case InteractiveFormActionType.CHANGE_INTERACTIVE_FILTERS:
            action.payload.filter.pageNumber = "1";
            return {
                ...currentState,
                filter: action.payload.filter as InteractiveFilters,
            };
        case InteractiveFormActionType.CLEAR_SORT:
            state.filter.pageNumber = "1";

            return {
                ...currentState,
                sort: DEFAULT_STATE.sort,
            };
        case InteractiveFormActionType.CHANGE_SORT:
            state.filter.pageNumber = "1";
            return {
                ...currentState,
                sort: O.fromNullable(action.payload.sort),
            };
        case InteractiveFormActionType.CLEAR_INTERACTIVE:
            state.filter = EmptyInteractiveFilter
            return {
                ...currentState,
                filter: action.payload.filter as InteractiveFilters,
            };
        default:
            return currentState;
    }
};
